// Copyright (c) HashiCorp, Inc
// SPDX-License-Identifier: MPL-2.0
import * as semver from "semver";

import {
  Errors,
  getGoVersion,
  getPythonVersion,
  getLanguage,
  getNodeVersion,
  getPackageVersion,
  logger,
  DISPLAY_VERSION,
  DISABLE_VERSION_CHECK,
} from "@cdktf/commons";

function throwIfLowerVersion(
  language: string,
  minVersion: string,
  stdout: string | undefined,
) {
  if (!stdout) {
    throw Errors.Usage(`${language} version could not be determined`);
  }

  const version = semver.coerce(stdout);
  if (!version || !semver.valid(version)) {
    console.error(
      Errors.Internal(`Unable to parse ${language} version "${stdout}"`),
    );
    return;
  }

  if (semver.lt(version, minVersion)) {
    console.error(
      Errors.Usage(
        `${language} version "${version}" is not supported. Please upgrade to at least ${minVersion}`,
      ),
    );
  }
}

async function checkGoVersion() {
  const out = await getGoVersion();
  throwIfLowerVersion("Go", "1.16.0", out);
}

async function checkPythonVersion() {
  const out = await getPythonVersion();
  throwIfLowerVersion("Python", "3.8.0", out);
}

async function checkNodeVersion() {
  const out = await getNodeVersion();
  throwIfLowerVersion("Node.js", "20.9.0", out);
}

export async function checkEnvironment() {
  await checkNodeVersion();

  switch (getLanguage()) {
    case "go":
      await checkGoVersion();
      break;
    case "python":
      await checkPythonVersion();
      break;
  }
}

export async function verifySimilarLibraryVersion() {
  if (DISABLE_VERSION_CHECK) {
    logger.debug("Version check disabled via environment variable");
    return;
  }

  const language = getLanguage();
  if (!language) {
    logger.debug("Unable to detect language, skipping version check");
    return;
  }

  const libVersion = await getPackageVersion(language, "cdktf");
  if (!libVersion) {
    logger.debug(`Unable to detect library version for ${language}`);
    return;
  }

  const cliVersion = `${DISPLAY_VERSION}`;

  if (!libVersion) {
    logger.debug(`Unable to detect library version for ${language}`);
    return;
  }

  logger.debug(`CLI version: ${cliVersion}`);
  logger.debug(`${language} package version: ${libVersion}`);

  if (cliVersion === "0.0.0" || cliVersion.includes("-dev")) {
    logger.debug(
      `Running a development version of cdktf, skipping version check`,
    );
    return;
  }

  if (cliVersion.includes(".dev") || cliVersion.includes("-pre.")) {
    logger.debug(
      `Running a pre-release version of cdktf-cli, skipping version compatibility check`,
    );
    return;
  }

  if (libVersion.includes(".dev") || libVersion.includes("-pre.")) {
    logger.debug(
      `Running a pre-release version of cdktf, skipping version compatibility check`,
    );
    return;
  }

  if (!semver.valid(libVersion)) {
    logger.info(
      "Could not determine library version, skipping version compatibility check",
    );
    return;
  }

  if (!semver.valid(cliVersion)) {
    logger.info(
      "Could not determine CLI version, skipping version compatibility check",
    );
    return;
  }

  if (semver.major(libVersion) !== semver.major(cliVersion)) {
    throw Errors.Usage(
      `The major version of the library (${libVersion}) and the CLI (${cliVersion}) are different. Please update the library to the same major version and regenerate your provider bindings with 'cdktf get' and update your prebuilt providers. For more information see https://cdk.tf/troubleshooting/cdktf-version-mismatch`,
    );
  }

  if (semver.minor(libVersion) !== semver.minor(cliVersion)) {
    throw Errors.Usage(
      `The minor version of the library (${libVersion}) and the CLI (${cliVersion}) are different. Please update the library to the same minor version and regenerate your provider bindings with 'cdktf get' and update your prebuilt providers. For more information see https://cdk.tf/troubleshooting/cdktf-version-mismatch`,
    );
  }
}

export function isInteractiveTerminal() {
  return process.stdout.isTTY && !process.env.CI;
}
